import AccessWrapper from "components/Modules/AccessWrapper";
import AutoCompleteBox from "components/Elements/EMIChip/AutoCompleteBox";
import ColourConstants from "helpers/colourConstants";
import CommonModal from "./CommonModal";
import DeleteDialog from "components/Elements/DeleteDialog";
import GeneralButton from "components/Elements/GeneralButton";
import ImportFileDialouge from "./ImportFileDialog";
import ModalAwaitingImports from "./ModalAwaitingImports";
import ModelsListTable from "./ModelsListTable";
import RestoreModalDialog from "./RestoreModalDialog";
import RoleWrapper from "components/Modules/RoleWrapper";
import ShareModal from "./ShareModal";
import TabTitle from "components/Elements/TabTitle";
import Typography from "@mui/material/Typography";
import VersionListTable from "./VersionListTable";
import mainAccess from "helpers/access";
import roles from "helpers/roles";
import updateStorage from "helpers/updateStorage";
import useSuperAdminExclude from "hooks/useSuperAdminExclude";
import withMount from "components/HOC/withMount";
import { Apis } from "services/api";
import { ChipTypeEnums, ModelListChipTypeEnums } from "constants/enums";
import { CircularProgress, LinearProgress } from "@mui/material";
import { createTableSortHandler, defaultPageSize } from "helpers/utils";
import { getSiteApplicationDetail } from "services/clients/sites/siteApplications/siteApplicationDetails";
import { getSiteDepartmentsInService } from "services/services/serviceLists";
import { handleSiteAppClick } from "helpers/handleSiteAppClick";
import { makeStyles } from "tss-react/mui";
import { showError } from "../../../redux/common/actions";
import { useDispatch } from "react-redux";
import { useFilterInitialization } from "components/Elements/Filters/Dropdown/hooks/useFilterInitialization";
import { useOutletContext } from "react-router-dom";
import { useRef } from "react";
import { useState, useCallback, useEffect, useMemo } from "react";
import {
	addModal,
	duplicateModal,
	getDeletedModels,
	getListOfModelVersions,
	getModelAutocompleteSearch,
	getModelImports,
	getModelList,
	getCountOfModelList,
	getModelStatuses,
	transferModel,
} from "services/models/modelList";
import {
	FilterContainer,
	FilterProvider,
	useFilters,
} from "components/Elements/Filters";

const media = "@media(max-width: 414px)";

const useStyles = makeStyles()((theme) => ({
	listActions: {
		marginBottom: 30,
	},
	headerContainer: {
		display: "flex",
		[media]: {
			flexDirection: "column",
		},
	},
	headerText: {
		fontSize: 21,
	},
	buttonContainer: {
		display: "flex",
		marginLeft: "auto",
		flexWrap: "wrap",
		[media]: {
			marginLeft: 0,
		},
	},
	importButton: {
		background: "#ED8738",
		width: "50%",
	},
	productButton: {
		backgroundColor: ColourConstants.confirmButton,
		color: "#FFFFFF",
		fontFamily: "Roboto Condensed",
		width: 150,
		fontSize: "13.5px",
		fontWeight: "bold",
		marginRight: "10px",
	},
	loading: {
		position: "absolute",
		width: "100%",
		left: 0,
		top: 0,
	},
	filter: {
		display: "flex",
	},
}));

const debounce = (func, delay) => {
	let timer;
	return function () {
		let self = this;
		let args = arguments;
		clearTimeout(timer);
		timer = setTimeout(() => {
			func.apply(self, args);
		}, delay);
	};
};


// Filter configuration for the new Filter framework
const filterConfig = {
	filters: {
		modelStatus: true,
		department: { multiSelect: true },
		tableSort: true,
		chips: {
			enabled: true,
			hiddenTypes: [
				ChipTypeEnums.SiteDepartmentID,
				ChipTypeEnums.ModelStatusID,
			], // Hide department and status chips since they're handled by filters
		},
	},
};

const ModelListsContent = ({ getError, isMounted }) => {
	const { classes } = useStyles();
	const { filterValues, updateFilter, allFiltersInitialized } = useFilters();

	// Helper to get current filter values for API calls
	const getCurrentFilterValues = () => {
		const statusValue = filterValues["modelStatus"]?.value;
		const departmentParams = filterValues["department"]?.params || {};
		const tableSortParams = filterValues["tableSort"]?.params;

		return {
			modelStatusID: statusValue?.id || "",
			...departmentParams,
			sortField: tableSortParams?.sortField || "",
			sortOrder: tableSortParams?.sortOrder || "asc",
		};
	};
	useSuperAdminExclude();
	//Init State
	const [isLoading, setIsLoading] = useState(true);
	const dispatch = useDispatch();
	const [deleteID, setDeleteID] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openVersionTableDialog, setOpenVersionTableDialog] = useState(false);
	const [openAddNewModal, setOpenAddNewModal] = useState(false);
	const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
	const [openShareModel, setOpenShareModel] = useState(false);
	const [duplicateModalData, setDuplicateModalData] = useState({});
	const [shareModelData, setShareModellData] = useState({});
	const [modelVersions, setModelVersions] = useState([]);
	const [allData, setAllData] = useState([]);
	const [filteredData, setFilteredData] = useState([]);
	const [isVersionTableLoading, setVersionTableLoading] = useState(false);
	const [openImportFile, setOpenImportFile] = useState(false);
	const [openRestoreModal, setOpenRestoreModal] = useState(false);
	const [modelImportData, setModelImportData] = useState([]);
	const [deletedModelData, setDeletedModelData] = useState([]);

	const [searching, setSearching] = useState(false);
	const [activeModelVersion, setActiveModelVersion] = useState(null);
	const [modelStatuses, setModelStatuses] = useState([]);
	const [siteDepartments, setSiteDepartments] = useState([]);
	const [siteAppState, setSiteAppState] = useState(null);
	// Get current table sort from FilterFramework (convert to array format for table component)
	const tableSortParams = filterValues.tableSort?.params || {
		sortField: "name",
		sortOrder: "asc",
	};
	const currentTableSort = [
		tableSortParams.sortField,
		tableSortParams.sortOrder,
	];
	// refs
	const searchRef = useRef("");
	const apiRef = useRef({});
	// Initialize chips using the generic useFilterInitialization hook
	useFilterInitialization("chips", [], {
		getDefaultValue: () => [],
		getParams: (chips) => ({ chips }),
		waitForData: false, // Don't wait for dataSource since chips start empty
	});

	// Helper function to extract valid department IDs (same as Defects pattern)
	const getValidDepartmentIds = (departmentValue) => {
		if (!departmentValue) return [];
		const depts = Array.isArray(departmentValue) ? departmentValue : [departmentValue];
		return depts
			.filter(d => d?.id && d.id !== '' && d.id !== 'all')
			.map(d => d.id);
	};

	// Create updated chips with department chips (same pattern as Defects)
	const updatedChips = useMemo(() => {
		const currentChips = filterValues.chips?.value || [];
		const hiddenTypes = [ChipTypeEnums.SiteDepartmentID, ChipTypeEnums.ModelStatusID];

		// Preserve all non-hidden chips
		const preservedChips = currentChips.filter(chip => !hiddenTypes.includes(chip.chipType));

		// Add department chips
		const departmentValue = filterValues.department?.value;
		const validDeptIds = getValidDepartmentIds(departmentValue);
		const departmentChips = validDeptIds.map(id => ({
			chipType: ChipTypeEnums.SiteDepartmentID,
			id,
			value: "",
		}));

		return [...preservedChips, ...departmentChips];
	}, [filterValues.chips?.value, filterValues.department?.value]);

	// Use updatedChips instead of chips from filter values
	const chips = updatedChips;

	// Local state for chip management
	const [isDropDownChanging, setDropDownChange] = useState(false);
	const [isRemovingChip, setIsRemovingChip] = useState(false);
	const [dataForFetchingDefect, setDataForFetchingDefect] = useState({
		pageNumber: 1,
		pageSize: defaultPageSize(),
		search: "",
		sortField: "",
		sort: "",
	});
	const [autocompleteData, setAutocompleteData] = useState([]);
	const [countOfModel, setCountOfModel] = useState(0);
	const tableRef = useRef(null);

	// Handle autocomplete contents - integrate with Filter Framework
	const handleAutoCompleteContent = async (item) => {
		// Reset dropdown state for consistency with old hook
		setDropDownChange(false);

		// Map chip type to backend enum - support both legacy 'type' and new 'chipType' properties
		const mappedChip = {
			chipType:
				item.chipType ||
				(item.type === "modelId"
					? ChipTypeEnums.ModelID // ModelID = 5
					: item.type === "siteAssetId"
					? ChipTypeEnums.SiteAssetID // SiteAssetID = 9
					: item.type === "modelTypeId"
					? ChipTypeEnums.ModelTypeID // ModelTypeID = 12
					: ChipTypeEnums.Undefined), // Default to Undefined = 0
			id: item.id || null,
			value: item.value || "",
		};

		// Enhanced duplicate checking - support both type and chipType for compatibility
		const currentChips = filterValues.chips?.value || [];
		const isDuplicate = currentChips.some(
			(existingItem) =>
				existingItem.id === mappedChip.id &&
				(existingItem.chipType === mappedChip.chipType ||
					existingItem.type === item.type) &&
				existingItem.value === mappedChip.value
		);

		if (!isDuplicate) {
			const updatedChips = [...currentChips, mappedChip];
			updateFilter("chips", updatedChips, { chips: updatedChips });
		}

		// Reset search field and autocomplete data (consistent with old hook)
		setAutocompleteData([]);
		setDataForFetchingDefect({
			pageNumber: 1,
			pageSize: defaultPageSize(),
			search: "",
			sortField: "",
			sort: "",
		});

		// Clear the search input
		searchRef.current = "";
	};

	const handleRemoveChip = async (removedChip) => {
		// Set loading state for user feedback
		setIsRemovingChip(true);
		setDropDownChange(false);

		try {
			// Filter based on item properties, not index
			const currentChips = filterValues.chips?.value || [];
			const updatedChips = currentChips.filter((item) => {
				// For proper comparison, check both id and chipType
				return !(
					item.id === removedChip.id && item.chipType === removedChip.chipType
				);
			});

			// Update the Filter Framework
			updateFilter("chips", updatedChips, { chips: updatedChips });
		} finally {
			// Reset loading state
			setIsRemovingChip(false);
		}
	};
	const me =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));
	const {
		position,
		application,
		customCaptions,
		siteID,
		siteAppID,
		site: { siteDepartmentID, siteDepartmentName, clientID },
	} = me;

	const [shareModelsFromApi, setShareModelsFromApi] = useState(0);
	useEffect(() => {
		const fetchClient = async () => {
			await dispatch(handleSiteAppClick(siteAppID, true));
			setShareModelsFromApi(application.shareModels);
		};
		fetchClient();
	}, [JSON.stringify({ clientID, application, siteAppID })]);

	const isSharableModel = shareModelsFromApi === 1 || shareModelsFromApi === 2;
	const { access } = useOutletContext();

	//display error popup
	const displayError = useCallback(
		(errorMessage, response) =>
			dispatch(
				showError(
					response?.data?.detail || errorMessage || "Something went wrong"
				)
			),
		[dispatch]
	);

	//DELETE
	const handleDeleteDialogOpen = (id) => {
		setDeleteID(id);

		setOpenDeleteDialog(true);
	};

	const closeDeleteDialog = () => setOpenDeleteDialog(false);

	const handleRemoveData = (id) => {
		const newData = [...allData.filter((d) => d.id !== id)];

		setFilteredData(newData);
		setAllData(newData);
	};

	//handle search
	const handleSearch = debounce(
		async (searchValue) => {
			setSearching(true);
			searchRef.current = searchValue;
			const autoCompleteRes = await getModelAutocompleteSearch({
				search: searchValue,
			});
			setAutocompleteData(autoCompleteRes?.data);
			if (!searchValue || searchValue === "")
				setDataForFetchingDefect({
					pageNumber: 1,
					pageSize: defaultPageSize(),
					search: "",
					sortField: "",
					sort: "",
				});

			setSearching(false);
		},
		[1000]
	);

	const onDuplicateModalOpen = (modalToDuplicate) => {
		setDuplicateModalData(modalToDuplicate);
		setOpenDuplicateModal(true);
	};
	const onShareModelOpen = (modalToShare) => {
		setShareModellData(modalToShare);
		setOpenShareModel(true);
	};

	const fetchModelList = useCallback(
		async ({
			searchTxt,
			modelStatusID = "",
			siteDepartmentID = "",
			chips,
			sortField = "",
			sortOrder = "",
			isActive = { current: true },
		}) => {
			// only allow chips that are currently in use - EMI3FE-167
			const availableChips = chips.filter((chip) =>
				Object.values(ModelListChipTypeEnums).includes(chip.chipType)
			);

			// Fetch list and count in parallel (like Services pattern)
			const [listResponse, countResponse] = await Promise.all([
				getModelList({
					id: position?.siteAppID || 24,
					pageNumber: 1,
					pageSize: defaultPageSize(),
					search: searchTxt,
					modelStatusId: modelStatusID,
					siteDepartmentId: siteDepartmentID,
					chips,
					sortField,
					sortOrder,
				}),
				getCountOfModelList({
					id: position?.siteAppID || 24,
					search: searchTxt,
					modelStatusId: modelStatusID,
					siteDepartmentId: siteDepartmentID,
					chips,
					sortField,
					sortOrder,
				}),
			]);

			// Only update state if this request is still active and component is mounted
			if (!isMounted.aborted && isActive.current) {
				if (listResponse.status) {
					const newData = listResponse.data.map((d) => ({
						...d,
						serialNumberRange: d?.serialNumberRange ?? "",
						fullName:
							d?.name +
							" " +
							(d?.modelName === null || d?.modelName === undefined
								? ""
								: d?.modelName),
					}));

					setAllData(newData);
					setFilteredData(newData);
					setDataForFetchingDefect((prev) => ({ ...prev, pageNumber: 1 }));

					// Set count from separate count endpoint
					if (countResponse?.status) {
						setCountOfModel(countResponse.data);
					} else {
						// If count endpoint fails, log error and use data length as fallback
						console.error("Failed to fetch model count:", countResponse);
						setCountOfModel(newData.length);
					}

					// Scroll to top when filters change
					if (tableRef.current?.gotoTop) {
						tableRef.current.gotoTop();
					}
				} else {
					displayError(listResponse?.data?.errors?.siteAppId[0], listResponse);
				}

				setIsLoading(false);
			}
			setSearching(false);
		},
		[position?.siteAppID, displayError]
	);

	const createModal = async (payload) => {
		return await addModal(payload);
	};

	const onDuplicateModal = async (payload) => {
		return await duplicateModal({
			modelId: duplicateModalData?.id,
			modelVersionId: duplicateModalData?.devModelVersionID,
			name: payload?.name,
			modelName: payload?.modelName,
			modelTypeId: payload?.modelTypeID,
			type: payload?.type,
			serialNumberRange: payload?.serialNumberRange,
		});
	};
	const onShareModel = async (payload) => {
		return await transferModel(payload);
	};

	const onViewVersionTableOpen = async (id) => {
		setOpenVersionTableDialog(true);
		setVersionTableLoading(true);
		setActiveModelVersion(
			allData.find((model) => model.id === id).activeModelVersion
		);

		const response = await getListOfModelVersions(id);

		if (response.status) {
			setModelVersions(response.data);
		} else {
			displayError(response?.data?.errors?.modelId[0], response);
			setOpenVersionTableDialog(false);
		}

		setVersionTableLoading(false);
	};

	const fetchModelImports = useCallback(async () => {
		const response = await getModelImports(position?.siteAppID || 24);
		if (!isMounted.aborted) {
			if (response.status) {
				setModelImportData(response?.data);
			} else {
				displayError(response?.data?.errors?.siteAppId[0], response);
			}
		} else {
			return;
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	//deleted model data
	const fetchDeletedModels = useCallback(async () => {
		const response = await getDeletedModels();
		if (!isMounted.aborted) {
			if (response.status) {
				setDeletedModelData(response?.data);
			} else {
				displayError(response?.data?.errors?.siteAppId[0], response);
			}
		} else {
			return;
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	//to get the state of current application--applicatioin.showLubricants and so on.
	const fetchSiteApplicationDetails = async () => {
		const result = await getSiteApplicationDetail(siteAppID);
		setSiteAppState(result);
	};

	useEffect(() => {
		fetchSiteApplicationDetails();
		if (siteAppID) updateStorage(siteAppID);
		const fetchData = async () => {
			const [, , modelStats, siteDepts] = await Promise.all([
				fetchModelList({
					chips,
					modelStatusID: "",
					siteDepartmentID: "",
					searchTxt: searchRef.current,
				}),
				fetchModelImports(),
				getModelStatuses(siteAppID),
				getSiteDepartmentsInService(siteID),
			]);
			if (modelStats.status) {
				setModelStatuses([{ id: "", name: "Show All" }, ...modelStats.data]);
			}
			if (siteDepts.status) {
				setSiteDepartments([{ id: "", name: "Show All" }, ...siteDepts.data]);
			}
		};
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Effect to handle filter changes only when all filters are initialized
	useEffect(() => {
		if (!allFiltersInitialized) {
			return;
		}

		// Track if this effect is still active to prevent race conditions
		const isActive = { current: true };

		const handleFilterChange = async () => {
			// Reset page to 1 BEFORE fetching (prevents infinite scroll race conditions)
			setDataForFetchingDefect((prev) => ({
				...prev,
				pageNumber: 1,
			}));

			setSearching(true);

			// Clear data and count BEFORE fetch to prevent infinite scroll from using stale values
			setAllData([]);
			setFilteredData([]);
			setCountOfModel(0);

			const params = getCurrentFilterValues();
			await fetchModelList({
				searchTxt: searchRef.current,
				...params,
				chips,
				isActive,
			});
			// Only clear searching state if this effect is still active
			if (isActive.current) {
				setSearching(false);
			}
		};

		handleFilterChange();

		// Cleanup: mark this effect as inactive when dependencies change
		return () => {
			isActive.current = false;
		};
	}, [filterValues, allFiltersInitialized, chips, fetchModelList]);
	// for deleted model
	useEffect(() => {
		if (position?.allowPublish) {
			fetchDeletedModels();
		}
	}, [position?.allowPublish]);

	//to set the table header and column
	const ModelListHeader = [
		// customCaptions?.make,
		// customCaptions?.model,
		customCaptions?.modelTemplate,
		customCaptions?.modelType,
		"Status",
		"Latest Version",
		"Active Version",
		customCaptions?.departmentPlural ?? "Departments",
		"Date Created",
		"Modified By",
		"Date Modified",
		"Review Date",
	];
	//
	const DeletedModelListHeader = [
		customCaptions?.modelTemplate,
		customCaptions?.modelType,
		"Restore",
	];
	const DeletedModelListColumn = ["name", "modelType", "Restore"];
	const ModelListColumn = [
		"fullName",
		"modelType",
		"status",
		"devModelVersion",
		"activeModelVersion",
		"departments",
		"createdDate",
		"modifiedUser",
		"modifiedDate",
		"reviewDate",
	];
	//for fetching the chips
	useEffect(() => {
		if (dataForFetchingDefect?.search.length >= 3) {
			setSearching(true);
			handleSearch(dataForFetchingDefect?.search);
		} else {
			setAutocompleteData([]);
		}
	}, [dataForFetchingDefect?.search]);
	//Hide Serial Number Range column of model list if the Application.ShowSerialNumberRange property is false.
	if (siteAppState?.data?.application?.showSerialNumberRange) {
		ModelListHeader.splice(5, 0, customCaptions?.serialNumberRange);
		ModelListColumn.splice(5, 0, "serialNumberRange");
	}

	// Create a memoized sort handler to prevent duplicate calls using the helper function
	const handleTableSort = useCallback(
		(sortField, sortOrder) => {
			setSearching(true);
			createTableSortHandler(tableSortParams, updateFilter)(
				sortField,
				sortOrder
			);
			// We don't need to fetch here as the filter change will trigger the useEffect
			setSearching(false);
		},
		[tableSortParams, updateFilter]
	);

	return (
		<div className="container">
			<TabTitle title={`${customCaptions.model} | ${application.name}`} />
			<CommonModal
				open={openAddNewModal}
				closeHandler={() => setOpenAddNewModal(false)}
				siteId={position?.siteAppID}
				data={null}
				title="Add "
				createProcessHandler={createModal}
			/>
			<CommonModal
				open={openDuplicateModal}
				closeHandler={() => setOpenDuplicateModal(false)}
				siteId={position?.siteAppID}
				data={duplicateModalData}
				title={"Duplicate "}
				createProcessHandler={onDuplicateModal}
				isDuplicate
			/>
			<ShareModal
				open={openShareModel}
				closeHandler={() => setOpenShareModel(false)}
				siteId={position?.siteAppID}
				data={shareModelData}
				title={"Transfer "}
				createProcessHandler={onShareModel}
				isSharableModel={shareModelsFromApi}
			/>
			<DeleteDialog
				entityName="Model"
				open={openDeleteDialog}
				closeHandler={closeDeleteDialog}
				deleteID={deleteID}
				deleteEndpoint={Apis.Models}
				deleteMsg="Deleting this model will remove all scheduled and in-progress services associated with it."
				handleRemoveData={handleRemoveData}
			/>
			<VersionListTable
				open={openVersionTableDialog}
				isLoading={isVersionTableLoading}
				closeHandler={() => setOpenVersionTableDialog(false)}
				versions={modelVersions}
				activeModelVersion={activeModelVersion}
			/>
			<ImportFileDialouge
				open={openImportFile}
				handleClose={() => setOpenImportFile(false)}
				importSuccess={fetchModelImports}
				getError={getError}
				siteAppID={position?.siteAppID}
			/>
			<RestoreModalDialog
				open={openRestoreModal}
				handleClose={() => setOpenRestoreModal(false)}
				importSuccess={fetchDeletedModels}
				getError={getError}
				headers={DeletedModelListHeader}
				columns={DeletedModelListColumn}
				data={deletedModelData}
				isMounted={isMounted}
			/>
			<div className={classes.listActions}>
				<div className={classes.headerContainer}>
					<Typography
						className={classes.headerText}
						component="h1"
						gutterBottom
					>
						{countOfModel === 0 ? (
							<strong>{` ${customCaptions?.model} List `}</strong>
						) : (
							<strong>{`${customCaptions?.model} List (${countOfModel})`}</strong>
						)}
					</Typography>

					<div className={classes.buttonContainer}>
						{deletedModelData?.length > 0 && position?.allowPublish && (
							<GeneralButton
								style={{ backgroundColor: "#ed8738" }}
								onClick={() => setOpenRestoreModal(true)}
							>
								Restore Model
							</GeneralButton>
						)}
						<RoleWrapper roles={[roles.siteUser]}>
							<AccessWrapper access={access} accessList={["E"]}>
								<GeneralButton
									style={{ backgroundColor: "#ed8738" }}
									onClick={() => setOpenImportFile(true)}
								>
									IMPORT FROM EXISTING
								</GeneralButton>
							</AccessWrapper>
						</RoleWrapper>

						<AccessWrapper access={access} accessList={["E"]}>
							<GeneralButton
								style={{ backgroundColor: "#23bb79" }}
								onClick={() => setOpenAddNewModal(true)}
							>
								ADD NEW
							</GeneralButton>
						</AccessWrapper>
					</div>
				</div>
				<ModalAwaitingImports
					modelImportData={modelImportData}
					customCaptions={customCaptions}
					isReadOnly={position?.[mainAccess.modelAccess] === "R"}
				/>
				<AutoCompleteBox
					searchQuery={dataForFetchingDefect?.search}
					setSearchQuery={(e) => {
						e.persist();
						setDataForFetchingDefect((prev) => ({
							...prev,
							search: e.target.value,
						}));
					}}
					width="100%"
					required
					label="Model Access"
					setSelectedValue={handleAutoCompleteContent}
					selectdValueToshow="name"
					dataSource={autocompleteData}
					isSearching={searching}
					chips={chips}
				/>

				{/* Filter Framework */}
				<div style={{ marginTop: 20 }}>
					<FilterContainer />
				</div>
			</div>
			{(searching || isRemovingChip) && <LinearProgress />}
			{isLoading ? (
				<CircularProgress />
			) : (
				<ModelsListTable
					ref={tableRef}
					position={position}
					data={filteredData}
					access={access}
					headers={
						application?.allowIndividualAssetModels
							? ModelListHeader
							: ModelListHeader
					}
					columns={ModelListColumn}
					setData={setFilteredData}
					handleSort={handleTableSort}
					handleDeleteDialogOpen={handleDeleteDialogOpen}
					handleDuplicateModalOpen={onDuplicateModalOpen}
					handleShareModelOpen={onShareModelOpen}
					handleViewVersionModalOpen={onViewVersionTableOpen}
					isSharableModel={isSharableModel}
					currentTableSort={currentTableSort}
					page={dataForFetchingDefect.pageNumber}
					countOfModel={countOfModel}
					searchText={searchRef.current}
					setDataForFetchingModel={setDataForFetchingDefect}
					chips={chips}
				/>
			)}
		</div>
	);
};

// Wrapper component that provides FilterContext
const ModelLists = (props) => {
	return (
		<FilterProvider config={filterConfig} storageKey="models">
			<ModelListsContent {...props} />
		</FilterProvider>
	);
};

export default withMount(ModelLists);
